/**
 * @fileoverview Service para operações com documentos.
 *
 * Centraliza chamadas de API relacionadas a documentos,
 * incluindo criação, edição, upload de PDFs e assinatura digital.
 *
 * Suporta dois tipos de documentos:
 * - Documentos DocGO: criados via editor com campos dinâmicos
 * - Documentos anexados: PDFs/imagens enviados via upload
 *
 * @module services/DocumentsService
 * @see {@link module:typedef} para definições de tipos
 */

import API_V2_URL from '@/constants/API_V2_URL';
import axios from 'axios';

/**
 * Service de documentos.
 * @namespace documentsService
 */
const documentsService = {
  /**
   * Solicita URL de download de documento.
   *
   * @param {number} documentId - ID do documento
   * @returns {Promise<import('axios').AxiosResponse<{download_url: string}>>}
   */
  download(documentId) {
    return axios.get(`${API_V2_URL()}/folders/documents/${documentId}/download`);
  },

  /**
   * Busca lista de documentos.
   * @returns {Promise<import('axios').AxiosResponse>}
   * @deprecated Não implementado
   */
  getDocuments() {
    // return axios.get('folders/folders/received`);
  },

  /**
   * Busca detalhes de um documento (API V1).
   *
   * @param {number} id - ID do documento
   * @returns {Promise<import('axios').AxiosResponse<Document>>}
   */
  getDocument(id) {
    return axios.get(`folders/documents/${id}`);
  },

  /**
   * Busca despachos padrão disponíveis.
   *
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getDispatches() {
    return axios.get('documents/dispatches');
  },

  /**
   * Busca tipos de documento por texto.
   *
   * @param {string} query - Texto de busca
   * @returns {Promise<import('axios').AxiosResponse<DocumentType[]>>}
   */
  getDocumentTypes(query) {
    return axios.get(`${API_V2_URL()}/system/document_types/search?q=${query}`);
  },

  /**
   * Busca detalhes de um tipo de documento (API V1).
   *
   * @param {number} id - ID do tipo
   * @returns {Promise<import('axios').AxiosResponse<DocumentType>>}
   */
  getDocumentType(id) {
    return axios.get(`folders/document_types/${id}`);
  },

  /**
   * Cria novo documento DocGO na pasta.
   * Documento é criado em estado temporário/rascunho.
   *
   * @param {number} folderId - ID da pasta
   * @param {number} documentTypeId - ID do tipo de documento
   * @param {string} documentState - Estado inicial ('temp'|'draft'|'minute')
   * @returns {Promise<import('axios').AxiosResponse<Document>>}
   */
  createNewDocument(folderId, documentTypeId, documentState) {
    return axios.post(
      `${API_V2_URL()}/folders/${folderId}/documents/create_document`,
      {document: {document_type_id: documentTypeId, document_state: documentState}});
  },

  /**
   * Atualiza documento DocGO (conteúdo e campos).
   * Gera nova versão do documento.
   *
   * @param {Object} params - Parâmetros
   * @param {number} params.id - ID do documento
   * @param {string} params.raw - Conteúdo HTML do documento
   * @param {FilledFields} params.filledFields - Campos preenchidos
   * @param {string} params.documentState - Novo estado
   * @param {Object} params.geolocation - Dados de geolocalização
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  updateDocument({id, raw, filledFields, documentState, geolocation}) {
    return axios.put(
      `${API_V2_URL()}/folders/documents/${id}/update_document`,
      {
        document:
          {
            document_state: documentState,
            document_version: {
              document_raw: raw,
              filled_fields: filledFields,
            },
          },
        geolocation,
      });
  },

  /**
   * Cria documento a partir de upload de arquivo (PDF/imagem).
   * Suporta callback de progresso para barra de upload.
   *
   * @param {number} folderId - ID da pasta
   * @param {FormData} body - FormData com arquivo
   * @param {Function} onUploadProgress - Callback de progresso
   * @returns {Promise<import('axios').AxiosResponse<Document>>}
   */
  async createNewPDFDocument(folderId, body, onUploadProgress) {
    return await axios.post(
      `${API_V2_URL()}/folders/${folderId}/documents/create_file`,
      body,
      {onUploadProgress},
    );
  },

  /**
   * Atualiza documento PDF existente (substitui arquivo).
   *
   * @param {number} documentId - ID do documento
   * @param {File} body - Novo arquivo
   * @param {Object} geolocation - Dados de geolocalização
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  async updatePDFDocument(documentId, body, geolocation) {
    return await (axios.put(
      `${API_V2_URL()}/folders/documents/${documentId}/update_file`,
      {
        document: {0: body},
        geolocation,
      }));
  },

  /**
   * Remove arquivo PDF de um documento.
   *
   * @param {number} documentId - ID do documento
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  destroyPDFDocument(documentId) {
    return axios.delete(`${API_V2_URL()}/folders/documents/${documentId}/destroy_file`);
  },

  /**
   * Remove documento completamente (soft delete).
   *
   * @param {number} id - ID do documento
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  removeDocument(id) {
    return axios.delete(`${API_V2_URL()}/folders/documents/${id}/destroy_document`);
  },

  /**
   * Assina documento digitalmente via SignGO.
   * Requer senha do usuário no SignGO e geolocalização.
   *
   * @param {number} documentId - ID do documento
   * @param {string} password - Senha SignGO do usuário
   * @param {Object} geolocation - Dados de geolocalização
   * @param {number} geolocation.latitude - Latitude
   * @param {number} geolocation.longitude - Longitude
   * @param {number} geolocation.accuracy - Precisão em metros
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  signDocument(documentId, password, geolocation) {
    return axios.post(`${API_V2_URL()}/folders/documents/sign_document`, {
      document_id: documentId,
      geolocation,
      password,
    });
  },
};

export default documentsService;



// WEBPACK FOOTER //
// ./src/services/DocumentsService.js